import {
  Component,
  Element,
  Event,
  EventEmitter,
  Fragment,
  h,
  Host,
  Listen,
  Method,
  Prop,
  State,
  Watch
} from "@stencil/core";
import { applySchemeDelegate, ColorableComponent, setCssVariablesDelegate } from "../../common/colorable-component";
import { LocalizableComponent, Localization, setCurrentLocalizationDelegate } from "../../common/localizable-component";
import { LinkLocalization, LinkSchemeDefinition, LinkSchemeName, LinkSize, LinkTarget } from "./constants/link-types";
import { LINK_SCHEME_CONFIG, LINK_SCHEME_MAP, LINKSIZE_NORMAL, LINKSIZE_SMALL } from "./constants/link-constants";
import { computeLinkAriaLabel, ComputeLinkIcon, ICON_DEFAULT } from "../../utils/link-helper";
import pfx from "../../utils/style-class-prefix-helper";
import { openModal } from "../../utils/openModal";
import { LINK_LOCALIZATION } from "./constants/link-i18n";
import { KeyIds } from "../../constants/keys";
import { LinkSchemeConfiguration } from "../../constants/colors";
import {
  TOOLTIP_VISIBILITY_ALWAYS,
  TOOLTIP_VISIBILITY_ON_CLICK,
  TOOLTIP_VISIBILITY_ON_HOVER_AND_FOCUS
} from "../tooltip/constants/tooltip-constants";
import {
  LINK_DEFAULT_ICON_POSITION,
  LINK_ICON_POSITION_RIGHT,
  LINK_ICON_POSITIONS,
  LinkIconPosition
} from "../../constants/global-constants";
import { SvgType } from "../../common/svg-type";
import { SvgLoader } from "../../common/svg-loader";
import { getContextMenuProps } from "../../utils/context-menu-helper";
import { ConditionalWrapper } from "../../common/conditional-wrapper";
import { BackgroundColor } from "../background/constants/background-types";
import sanitizeHtml from "sanitize-html";
import { StyledComponent } from "../../decorators/styled-component";

// import { InitLogging } from "../../decorators/init-logging";

/**
 * @slot info-icon - Holds a single eon-ui-info-icon element
 * @slot context-menu - holds eon-ui-context-menu component
 * @slot tooltip - holds eon-ui-tooltip component
 */
@Component({
  tag: "envc-eon-ui-link",
  styleUrl: "link.scss",
  shadow: true
})
export class Link
  implements ColorableComponent<LinkSchemeConfiguration<LinkSchemeDefinition>>, LocalizableComponent<LinkLocalization> {
  // @InitLogging() sentryTransaction;
  schemeConfig: LinkSchemeConfiguration<LinkSchemeDefinition> = LINK_SCHEME_CONFIG;
  schemeMap = LINK_SCHEME_MAP;
  contextMenu: HTMLEnvcEonUiContextMenuElement;
  tooltipElement: HTMLEnvcEonUiTooltipElement;

  @State() activeScheme: LinkSchemeDefinition;
  @State() linkStyle: string;
  @State() isInfoIconAvailable: boolean;
  @State() isVisibilityOnClick: string;
  @State() currentIcon: string;
  @State() copiedToClipboard: boolean = false;
  @State() showTooltip: boolean = true;

  localization: Localization<LinkLocalization> = LINK_LOCALIZATION;
  currentLocalization: LinkLocalization;

  private readonly loader: SvgLoader;
  @State() currentAriaLabel: string;
  private rel: string = "";

  /**
   * Raised when the link has been clicked.
   */
  @Event() linkClick: EventEmitter;

  /**
   * Raised when the link has been loaded.
   */
  @Event() linkLoad: EventEmitter;

  @StyledComponent() component: any;
  @Element() hostElement: HTMLEnvcEonUiLinkElement;

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @Prop({ reflect: true }) scheme: LinkSchemeName;

  /**
   * Defines the target url of a Link.
   * Make sure to prefix this with a protocol if the link should display an icon automatically.
   * Valid protocols are "http://", "https://", "mailto:" and "tel:".
   * @example http://www.google.de, tel:0123456789
   */
  @Prop({ mutable: true }) href: string;

  /**
   * Defines the text shown as label for the link.
   */
  @Prop() text: string;

  /**
   * Specifies how the link is opened in the browser, e.g. in new window/tab.
   * @example "_blank", "_self", "_parent", "_top"
   */
  @Prop() target: LinkTarget;

  /**
   * If the automatically generated icon does not fit, you can override it with your own.
   * Possible icons are: "arrow_right", "phone", "notification", "social_x" "social_youtube", "social_facebook", "social_instagram" and "social_linked_in".
   * @example "phone".
   */
  @Prop() icon: string;

  /**
   * Whether this button can be focused by a keyboard. Defaults to true.
   */
  @Prop() navigable: boolean = true;

  /**
   * Specifies the label to use for accessibility.
   * Always provide this for pure icon buttons!
   */
  @Prop({ attribute: "a11y-aria-label", mutable: true }) a11yAriaLabel: string;

  /**
   * **Internal use only!**
   * &nbsp;<br>
   * if the link used inside the linklist the icon of the link should get a slightly wider container where it should be center-aligned.
   * **Important notes**: This property **must** not be used manually, i.e. only if the link is used within a linklist.<br>See documentation for the linklist.
   * @disableControlInStorybook true
   */
  @Prop() alignIconCenter: boolean;

  /**
   * If set to true, no icon will be shown. Default: false
   */
  @Prop() hideIcon: boolean;

  /**
   * If set to true, the "Download" icon will be used for the link and the browser will be forced to download the file instead of rendering it.
   * This is also the case if the href starts with "file:".
   */
  @Prop() download: boolean;

  /**
   * Determine if the link should be displayed as if it was hovered.
   */
  @Prop() forceHoverStyle: boolean;

  /**
   * The size of the link.
   */
  @Prop() size: LinkSize = LINKSIZE_NORMAL;

  /**
   * use this property to open a modal, the modal must have the same modal-id<br>
   * see component: Modal
   */
  @Prop() openModalId: string;

  /**
   * used to set icon position of the link. It can either be left or right.
   */
  @Prop({ reflect: true }) iconPosition: LinkIconPosition = LINK_DEFAULT_ICON_POSITION;

  /**
   * used to integrate links inside paragraphs.
   */
  @Prop() displayInline: boolean;

  /**
   * Used to Disable the action link.
   */
  @Prop() disabled: boolean;

  /**
   * Change link internally to button element. That's relevant for some js frameworks.
   */
  @Prop({ reflect: true }) useAsButton: boolean;

  /**
   * Whether the link should act as a copy to clipboard button.
   */
  @Prop() copyToClipboard: boolean = false;

  /**
   * Text to be copied to clipboard when the user clicks on the link. If this property is not set, the link text will be copied instead.
   * Only works if copyToClipboard is set to true.
   */
  @Prop() copyText: string;

  /**
   * This method can be called to re-trigger the automatic background color detection
   */
  @Method()
  async updateScheme(backgroundColor?: BackgroundColor) {
    applySchemeDelegate(this, backgroundColor);
  }

  constructor() {
    this.loader = new SvgLoader();
  }

  componentWillLoad() {
    this.deprecatedFallback();
    this.applyScheme();
    this.setCurrentLocalization();
    this.computeIcon();
    this.computeAriaLabel();
    this.setRelAttribute();
    this.initContextMenu();
    this.isInfoIconAvailable = !!this.hostElement.querySelector('[slot="info-icon"]');
    this.getTooltipElement();
    this.handleIconPosition();
  }

  componentDidLoad() {
    this.setCssVariables();
    this.linkLoad.emit();
  }

  deprecatedFallback() {
    this.a11yAriaLabel = this.hostElement.getAttribute("aria-label") ?? this.a11yAriaLabel;
  }

  @Watch("scheme")
  applyScheme(): void {
    applySchemeDelegate(this);
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
  }

  @Watch("target")
  setRelAttribute(): void {
    this.rel = this.target === "_blank" && "noopener noreferrer";
  }

  setCurrentLocalization() {
    setCurrentLocalizationDelegate(this);
  }

  @Watch("href")
  @Watch("a11yAriaLabel")
  @Watch("copyToClipboard")
  private computeAriaLabel() {
    if (this.a11yAriaLabel || !this.currentLocalization) {
      this.currentAriaLabel = this.a11yAriaLabel;
      return;
    }

    if (this.copyToClipboard) {
      this.currentAriaLabel = this.currentLocalization.copyToClipboardAria.replace(
        "{TEXT}",
        this.copyText || this.text
      );
      return;
    }

    if (this.openModalId) {
      this.currentAriaLabel = this.currentLocalization.ariaLabel.modal;
    } else {
      this.currentAriaLabel = computeLinkAriaLabel(this.currentLocalization, this.href, this.download);
    }
  }

  @Watch("iconPosition")
  handleIconPosition() {
    if (!LINK_ICON_POSITIONS.includes(this.iconPosition)) {
      this.iconPosition = LINK_DEFAULT_ICON_POSITION;
    }
  }

  @Watch("href")
  @Watch("icon")
  @Watch("hideIcon")
  @Watch("download")
  @Watch("displayInline")
  private computeIcon() {
    if (this.hideIcon || this.displayInline) {
      this.currentIcon = null;
      return;
    } else if (this.icon) {
      this.currentIcon = this.icon;
      this.fallbackIconCheckup(ICON_DEFAULT);
      return;
    } else if (this.copyToClipboard) {
      this.currentIcon = "copy";

      if (!this.hostElement.getAttribute("icon-position")) {
        this.iconPosition = "left";
      }
      return;
    }

    // Get icon based on the links target url
    this.currentIcon = ComputeLinkIcon(this.href, this.download, "link") as string;
    this.fallbackIconCheckup(ICON_DEFAULT);
  }

  /**
   * Check if icon does exist. If not, set to default.
   */
  fallbackIconCheckup(icon: string) {
    this.loader.requestSvg(this.currentIcon, SvgType.ICON).catch(() => {
      this.currentIcon = icon;
    });
  }

  handleClick(_event: Event) {
    if (!!this.openModalId) {
      openModal(this.openModalId);
    }

    if (this.contextMenu) {
      this.contextMenu.visible = true;
      this.hostElement.shadowRoot.querySelector("a").ariaExpanded = "true";
    }

    if (this.copyToClipboard) {
      navigator.clipboard.writeText(this.copyText || this.text).then(() => {
        this.copiedToClipboard = true;
        setTimeout(() => {
          this.showTooltip = false;

          // revert tooltip text after tooltip is invisible again.
          setTimeout(() => {
            this.copiedToClipboard = false;
          }, 1000);
        }, 3000);
      });
    }

    this.linkClick.emit();
  }

  private handleKeyDown(e: KeyboardEvent) {
    if (e.key == KeyIds.ENTER || e.key == KeyIds.SPACE) {
      // we prevent default scroll behaviour
      if (this.openModalId || this.contextMenu || this.tooltipElement) {
        e.preventDefault();
      }
      if (!!this.openModalId) {
        openModal(this.openModalId);
      }

      this.contextMenu && (this.contextMenu.visible = true);
      this.tooltipElement && (this.tooltipElement.visible = true);

      this.linkClick.emit();
    }
  }

  /**
   * Context Menu Setup including getter, click events, keyboard events and properties
   */
  private initContextMenu() {
    this.contextMenu = this.hostElement.querySelector('[slot="context-menu"]');
  }

  iconRender() {
    return (
      <div class={pfx("icon-wrapper")}>
        <envc-eon-ui-icon
          class={pfx("icon")}
          size={this.size === LINKSIZE_SMALL ? "small" : "normal"}
          name={this.currentIcon}
          noSpace={!this.alignIconCenter ? true : false}
          aria-hidden={"true"}
        />
      </div>
    );
  }

  /**
   * Tooltip Check for OnClick visibility property
   */
  private getTooltipElement() {
    this.tooltipElement = this.hostElement.querySelector('[slot="tooltip"]');
    this.isVisibilityOnClick = ((this.tooltipElement as unknown) as HTMLEnvcEonUiTooltipElement)?.visibility;
  }

  handleFocusEvent() {
    const tooltipElements = Array.from(document.querySelectorAll("envc-eon-ui-tooltip"));
    tooltipElements.map((item) => {
      this.hostElement.parentElement !== item &&
        item.visibility !== TOOLTIP_VISIBILITY_ALWAYS &&
        (item.visible = false);
    });
  }

  handleBlurEvent() {
    const tooltipElements = Array.from(document.querySelectorAll("envc-eon-ui-tooltip"));
    tooltipElements.map((item) => {
      this.hostElement.parentElement === item &&
        item.visibility !== TOOLTIP_VISIBILITY_ALWAYS &&
        (item.visible = false);
    });
  }

  @Listen("click", { target: "window" })
  onDocumentClick(event) {
    if (
      event.target !== this.hostElement &&
      event.target !== this.tooltipElement &&
      event.target !== this.hostElement.querySelector("envc-eon-ui-info-icon") &&
      event.target.parentElement !== this.tooltipElement
    ) {
      this.tooltipElement &&
        this.tooltipElement.visibility === TOOLTIP_VISIBILITY_ON_CLICK &&
        (this.tooltipElement.visible = false);
    }
  }

  private getTooltipProps() {
    return {
      onFocus: () => this.handleFocusEvent(),
      onBlur: () => this.handleBlurEvent()
    };
  }

  render() {
    return (
      <Host class={pfx({ ["inline"]: this.displayInline })}>
        <ConditionalWrapper
          condition={!!this.useAsButton || !!this.copyToClipboard}
          wrapper={(children) => (
            <button
              title={this.hostElement.getAttribute("title")}
              {...(!this.disabled ? { href: this.href, tabIndex: this.navigable ? 0 : -1 } : {})}
              class={pfx({
                ["link"]: true,
                hover: this.forceHoverStyle,
                ["size-normal"]: this.size === LINKSIZE_NORMAL,
                ["size-small"]: this.size === LINKSIZE_SMALL,
                [this.linkStyle]: this.linkStyle,
                [`visibility-${this.isVisibilityOnClick}`]: !!this.isVisibilityOnClick,
                ["disabled"]: !!this.disabled,
                ["autosize"]: !this.alignIconCenter,
                [`icon-position-${this.iconPosition}`]: true
              })}
              disabled={this.disabled}
              onClick={(event: MouseEvent) => this.handleClick(event)}
              onKeyDown={(event: KeyboardEvent) => this.handleKeyDown(event)}
              {...(this.download && { download: "" })}
              {...(this.contextMenu
                ? getContextMenuProps(this.hostElement, this.contextMenu.visible, this.currentAriaLabel)
                : this.currentAriaLabel && { "aria-label": this.currentAriaLabel })}
              {...(this.tooltipElement && this.getTooltipProps())}
            >
              {this.copyToClipboard && (
                <envc-eon-ui-tooltip
                  placement="bottom"
                  size="tiny"
                  visible={this.showTooltip}
                  visibility={TOOLTIP_VISIBILITY_ON_HOVER_AND_FOCUS}
                  aria-hidden={true}
                >
                  <div class={pfx("tooltip-wrapper")}>
                    {this.copiedToClipboard && <envc-eon-ui-icon name="radio_checkbox_tick" size="tiny" />}
                    <envc-eon-ui-text textStyle="copy-small">
                      {this.currentLocalization[this.copiedToClipboard ? "copiedToClipboard" : "copyToClipboard"]}
                    </envc-eon-ui-text>
                  </div>
                </envc-eon-ui-tooltip>
              )}

              {children}
            </button>
          )}
          defaultWrapper={(children) => (
            <Fragment>
              {/* eslint-disable-next-line jsx-a11y/interactive-supports-focus */}
              <a
                title={this.hostElement.getAttribute("title")}
                role={"link"}
                target={this.target}
                {...(!this.disabled ? { href: this.href, tabIndex: this.navigable ? 0 : -1 } : {})}
                class={pfx({
                  ["link"]: true,
                  hover: this.forceHoverStyle,
                  ["size-normal"]: this.size === LINKSIZE_NORMAL,
                  ["size-small"]: this.size === LINKSIZE_SMALL,
                  [this.linkStyle]: this.linkStyle,
                  [`visibility-${this.isVisibilityOnClick}`]: !!this.isVisibilityOnClick,
                  ["disabled"]: !!this.disabled,
                  ["autosize"]: !this.alignIconCenter,
                  [`icon-position-${this.iconPosition}`]: true
                })}
                rel={this.rel}
                onClick={(event: MouseEvent) => this.handleClick(event)}
                onKeyDown={(event: KeyboardEvent) => this.handleKeyDown(event)}
                {...(this.download && { download: "" })}
                {...(this.contextMenu
                  ? getContextMenuProps(this.hostElement, this.contextMenu.visible, this.currentAriaLabel)
                  : this.currentAriaLabel && { "aria-label": this.currentAriaLabel })}
                {...(this.tooltipElement && this.getTooltipProps())}
              >
                {children}
              </a>
            </Fragment>
          )}
        >
          {this.currentIcon && this.iconPosition !== LINK_ICON_POSITION_RIGHT && this.iconRender()}
          {this.text && (
            <div class={pfx("text-wrapper")}>
              <span class={pfx("text")} innerHTML={sanitizeHtml(this.text)} />
            </div>
          )}
          {this.currentIcon && this.iconPosition === LINK_ICON_POSITION_RIGHT && this.iconRender()}
        </ConditionalWrapper>
        {this.isInfoIconAvailable && !this.displayInline && (
          <div class={pfx("link-info-icon")}>
            <slot name="info-icon" />
          </div>
        )}
        <slot name="context-menu" />
        <slot name="tooltip" />
      </Host>
    );
  }
}
